import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ClipboardList, Shield, AlertTriangle, RefreshCw, 
  Building, Calendar, FileCheck, ChevronRight,
  ArrowUpRight, Clock, DollarSign, TrendingUp, X, ExternalLink
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

// Custom hook for safe fetching with retry
function useSafeFetch(endpoint) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.ok) {
        setData(res.data.data || []);
        setSummary(res.data.stage_summary || res.data.summary || null);
        setTotal(res.data.total || 0);
      } else {
        throw new Error(res.data.error || "Failed to load data");
      }
    } catch (err) {
      console.error("FETCH ERROR:", err);
      setError(err.response?.data?.detail || err.message || "Failed to load data");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, retry: fetchData, summary, total };
}

// Status badge component
function StatusBadge({ status }) {
  const colorMap = {
    // Job stages
    "Job Created": "bg-slate-500",
    "New": "bg-slate-500",
    "Unassigned": "bg-slate-400",
    "Assigned": "bg-sky-500",
    "Awaiting Quote": "bg-amber-500",
    "Quote Awaiting Approval": "bg-amber-600",
    "Approved": "bg-purple-600",
    "PO Issued": "bg-purple-500",
    "Scheduled": "bg-blue-600",
    "In Progress": "bg-blue-500",
    "Completed": "bg-green-600",
    "Paid": "bg-green-700",
    "Invoice Review": "bg-orange-500",
    "Approved for Invoicing": "bg-teal-600",
    "Reassign Required": "bg-red-600",
    // Compliance statuses
    "Compliant": "bg-green-600",
    "Non-Compliant": "bg-red-600",
    "Expired": "bg-red-700",
    "Urgent": "bg-red-500",
    "Warning": "bg-amber-500",
    "Upcoming": "bg-sky-500",
    "OK": "bg-green-500",
    "Overdue": "bg-red-700",
    // Case statuses
    "Failed": "bg-red-600",
    "Remedial Required": "bg-orange-600",
    "Quote Received": "bg-amber-500",
    "Works Complete": "bg-green-500",
    "Pass": "bg-green-600"
  };

  const bgColor = colorMap[status] || "bg-slate-600";
  
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-white ${bgColor}`}>
      {status || "-"}
    </span>
  );
}

// Table cell helper
function Cell({ value, className = "" }) {
  const display = value === null || value === undefined || value === "" ? "-" : value;
  return <span className={className}>{display}</span>;
}

export default function OperationalDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("jobs");
  const [statusFilter, setStatusFilter] = useState(null); // Status filter state
  const [selectedAsset, setSelectedAsset] = useState(null); // For compliance card click
  const [assetJobs, setAssetJobs] = useState([]); // Jobs for selected asset
  const [loadingAssetJobs, setLoadingAssetJobs] = useState(false);

  const jobs = useSafeFetch("/api/fm/jobs/dashboard/operational?limit=500");
  const compliance = useSafeFetch("/api/compliance/dashboard");
  const cases = useSafeFetch("/api/fm/compliance/cases");

  // Compute stage counts from loaded data (not from API summary which may include more)
  const stageCounts = jobs.data.reduce((acc, job) => {
    const stage = job.stage || 'Unknown';
    acc[stage] = (acc[stage] || 0) + 1;
    return acc;
  }, {});

  // Filter jobs by selected status
  const filteredJobs = statusFilter 
    ? jobs.data.filter(job => job.stage === statusFilter)
    : jobs.data;

  // Fetch jobs for a specific asset when card is clicked
  const handleAssetClick = async (asset) => {
    setSelectedAsset(asset);
    setLoadingAssetJobs(true);
    try {
      const token = localStorage.getItem("token");
      // Fetch jobs and filter by asset
      const res = await axios.get(`${API}/api/fm/jobs?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allJobs = res.data?.jobs || res.data || [];
      
      // Filter jobs matching this asset - check multiple fields
      const assetId = asset.asset_id;
      const assetName = asset.asset_name;
      const parkName = asset.park_name;
      // Extract unit number from asset name (e.g., "Unit 60 - Wyldecrest..." -> "60")
      const unitMatch = assetName?.match(/Unit\s+(\d+)/i);
      const unitNumber = unitMatch ? unitMatch[1] : null;
      
      // First try strict matching
      let matchingJobs = allJobs.filter(job => {
        // Match by asset_id
        if (job.asset_id === assetId) return true;
        // Match by asset_name
        if (job.asset_name === assetName) return true;
        // Match by site containing park name
        if (job.site && parkName && job.site.toLowerCase().includes(parkName.toLowerCase())) return true;
        // Match by unit number
        if (unitNumber && job.unit && job.unit.includes(unitNumber)) return true;
        return false;
      }).filter(job => 
        !['Completed', 'Closed', 'Cancelled'].includes(job.status)
      );
      
      // If no matches found and the card says there are open jobs,
      // show a message that jobs exist but aren't linked
      if (matchingJobs.length === 0 && asset.open_jobs_count > 0) {
        // Jobs exist but aren't linked to assets - show unlinked jobs for this park
        matchingJobs = allJobs.filter(job => {
          if (['Completed', 'Closed', 'Cancelled'].includes(job.status)) return false;
          // Show jobs that don't have asset linkage (likely seeded data)
          return !job.asset_id && !job.asset_name;
        }).slice(0, asset.open_jobs_count); // Limit to the count shown
      }
      
      setAssetJobs(matchingJobs);
    } catch (err) {
      console.error("Failed to load asset jobs:", err);
      setAssetJobs([]);
    } finally {
      setLoadingAssetJobs(false);
    }
  };

  // Jobs Dashboard Tab
  const renderJobs = () => {
    if (jobs.loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-teal-600 mr-2" />
          <span className="text-slate-600">Loading jobs...</span>
        </div>
      );
    }
    
    if (jobs.error) {
      return (
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 inline-block">
            <p className="text-red-700">{jobs.error}</p>
          </div>
          <div>
            <button 
              onClick={jobs.retry}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
            >
              <RefreshCw className="h-4 w-4 inline mr-1" /> Retry
            </button>
          </div>
        </div>
      );
    }

    if (!jobs.data.length) {
      return (
        <div className="text-center py-12 text-slate-500">
          <ClipboardList className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No jobs available</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Stage Summary Cards - CLICKABLE - Uses computed counts from loaded data */}
        {Object.keys(stageCounts).length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            {Object.entries(stageCounts)
              .sort(([,a], [,b]) => b - a) // Sort by count descending
              .map(([stage, count]) => (
              <div 
                key={stage} 
                onClick={() => setStatusFilter(statusFilter === stage ? null : stage)}
                className={`rounded-lg p-3 border cursor-pointer transition-all hover:shadow-md ${
                  statusFilter === stage 
                    ? 'bg-[#1F7A78] border-[#1F7A78] ring-2 ring-white' 
                    : 'bg-slate-50 border-slate-200 hover:border-[#1F7A78]'
                }`}
                data-testid={`status-tile-${stage.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className={`text-2xl font-bold ${statusFilter === stage ? 'text-white' : 'text-slate-800'}`}>
                  {count}
                </div>
                <div className={`text-xs truncate ${statusFilter === stage ? 'text-white/80' : 'text-slate-500'}`}>
                  {stage}
                </div>
                <div className={`text-[10px] mt-1 ${statusFilter === stage ? 'text-white/60' : 'text-slate-400'}`}>
                  {statusFilter === stage ? 'Click to clear' : 'Click to filter'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Active Filter Indicator */}
        {statusFilter && (
          <div className="flex items-center gap-2 px-3 py-2 bg-[#1F7A78]/10 border border-[#1F7A78]/20 rounded-lg">
            <span className="text-sm text-[#1F7A78] font-medium">
              Showing: {statusFilter} ({filteredJobs.length} jobs)
            </span>
            <button 
              onClick={() => setStatusFilter(null)}
              className="ml-auto text-xs text-[#1F7A78] hover:text-[#16615f] font-medium"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Jobs Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left p-3 font-semibold text-slate-700">Job ID</th>
                <th className="text-left p-3 font-semibold text-slate-700">Park</th>
                <th className="text-left p-3 font-semibold text-slate-700">Unit</th>
                <th className="text-left p-3 font-semibold text-slate-700">Job Type</th>
                <th className="text-left p-3 font-semibold text-slate-700">Stage</th>
                <th className="text-left p-3 font-semibold text-slate-700">Contractor</th>
                <th className="text-left p-3 font-semibold text-slate-700">Due Date</th>
                <th className="text-right p-3 font-semibold text-slate-700">Value</th>
                <th className="text-left p-3 font-semibold text-slate-700">Next Action</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((row, idx) => (
                <tr 
                  key={`${row.internal_id || row.job_id}-${idx}`}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/fm/jobs/${row.internal_id}`)}
                  data-testid={`job-row-${row.job_id}`}
                >
                  <td className="p-3 font-mono text-teal-700 font-medium">
                    <Cell value={row.job_id} />
                  </td>
                  <td className="p-3"><Cell value={row.park} /></td>
                  <td className="p-3"><Cell value={row.unit} /></td>
                  <td className="p-3"><Cell value={row.job_type} /></td>
                  <td className="p-3"><StatusBadge status={row.stage} /></td>
                  <td className="p-3"><Cell value={row.contractor} /></td>
                  <td className="p-3"><Cell value={row.due_date} /></td>
                  <td className="p-3 text-right font-medium">
                    £{Number(row.value || 0).toFixed(2)}
                  </td>
                  <td className="p-3 text-slate-600"><Cell value={row.next_action} /></td>
                  <td className="p-3">
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="text-sm text-slate-500 text-right">
          Showing {filteredJobs.length} of {jobs.total} jobs
        </div>
      </div>
    );
  };

  // Compliance Dashboard Tab
  const renderComplianceDashboard = () => {
    if (compliance.loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-teal-600 mr-2" />
          <span className="text-slate-600">Loading compliance data...</span>
        </div>
      );
    }
    
    if (compliance.error) {
      return (
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 inline-block">
            <p className="text-red-700">{compliance.error}</p>
          </div>
          <div>
            <button 
              onClick={compliance.retry}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
            >
              <RefreshCw className="h-4 w-4 inline mr-1" /> Retry
            </button>
          </div>
        </div>
      );
    }

    if (!compliance.data.length) {
      return (
        <div className="text-center py-12 text-slate-500">
          <Shield className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No compliance data available</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {compliance.data.map((asset, idx) => (
          <div 
            key={`${asset.asset_id}-${idx}`}
            onClick={() => handleAssetClick(asset)}
            className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-[#1F7A78] transition-all cursor-pointer"
            data-testid={`compliance-card-${asset.asset_id}`}
          >
            {/* Asset Header */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-slate-800">
                  {asset.asset_name || "Unnamed Asset"}
                </h3>
                <div className="text-sm text-slate-500 flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  {asset.park_name || "-"} · {asset.postcode || "-"}
                </div>
              </div>
              <div className="text-center bg-[#1F7A78] rounded-lg px-3 py-2 text-white">
                <div className="text-lg font-bold">
                  {asset.open_jobs_count || 0}
                </div>
                <div className="text-xs text-white/80">Open Jobs</div>
              </div>
            </div>

            {/* Overall Status */}
            <div className="mb-3">
              <StatusBadge status={asset.overall_status} />
            </div>

            {/* Compliance Items */}
            <div className="space-y-2">
              {(!asset.compliance_items || asset.compliance_items.length === 0) ? (
                <div className="text-sm text-slate-400 py-2">No certificates linked</div>
              ) : (
                asset.compliance_items.map((item, itemIdx) => (
                  <div 
                    key={`${item.certificate_type}-${itemIdx}`}
                    className="flex items-center justify-between bg-slate-50 rounded-lg p-2.5"
                  >
                    <div className="flex items-center gap-2">
                      <FileCheck className="h-4 w-4 text-slate-400" />
                      <span className="font-medium text-sm text-slate-700">
                        {item.certificate_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={item.status} />
                      <div className="text-xs text-slate-500 text-right min-w-[80px]">
                        {item.expiry_date ? (
                          <>
                            <div>{new Date(item.expiry_date).toLocaleDateString()}</div>
                            <div className="text-slate-400">
                              {item.days_to_expiry} days
                            </div>
                          </>
                        ) : "-"}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Click hint */}
            <div className="mt-3 text-xs text-[#1F7A78] text-center font-medium">
              Click to view {asset.open_jobs_count || 0} jobs
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Compliance Cases Tab
  const renderComplianceCases = () => {
    if (cases.loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-teal-600 mr-2" />
          <span className="text-slate-600">Loading compliance cases...</span>
        </div>
      );
    }
    
    if (cases.error) {
      return (
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 inline-block">
            <p className="text-red-700">{cases.error}</p>
          </div>
          <div>
            <button 
              onClick={cases.retry}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
            >
              <RefreshCw className="h-4 w-4 inline mr-1" /> Retry
            </button>
          </div>
        </div>
      );
    }

    if (!cases.data.length) {
      return (
        <div className="text-center py-12 text-slate-500">
          <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No compliance cases</p>
          <p className="text-sm text-slate-400 mt-1">Cases are created when PPM inspections fail</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left p-3 font-semibold text-slate-700">Case ID</th>
              <th className="text-left p-3 font-semibold text-slate-700">Park</th>
              <th className="text-left p-3 font-semibold text-slate-700">Asset</th>
              <th className="text-left p-3 font-semibold text-slate-700">Certificate</th>
              <th className="text-left p-3 font-semibold text-slate-700">Status</th>
              <th className="text-left p-3 font-semibold text-slate-700">Compliance</th>
              <th className="text-left p-3 font-semibold text-slate-700">Failed Date</th>
              <th className="text-left p-3 font-semibold text-slate-700">Deadline</th>
              <th className="text-center p-3 font-semibold text-slate-700">Days Left</th>
              <th className="text-left p-3 font-semibold text-slate-700">Deadline Status</th>
            </tr>
          </thead>
          <tbody>
            {cases.data.map((row, idx) => (
              <tr 
                key={`${row.id || row.case_id}-${idx}`}
                className="border-t border-slate-100 hover:bg-teal-50 cursor-pointer transition-colors"
                data-testid={`case-row-${row.case_id}`}
                onClick={() => {
                  // Navigate to the job if job_id exists
                  if (row.job_id) {
                    navigate(`/fm/jobs/${row.job_id}`);
                  } else if (row.parent_job_id) {
                    navigate(`/fm/jobs/${row.parent_job_id}`);
                  } else {
                    toast.info("No linked job found for this case");
                  }
                }}
              >
                <td className="p-3 font-mono text-teal-700 font-medium hover:underline">
                  <Cell value={row.case_id} />
                </td>
                <td className="p-3"><Cell value={row.park_name} /></td>
                <td className="p-3"><Cell value={row.asset_name} /></td>
                <td className="p-3"><Cell value={row.certificate_type || row.certificate_name} /></td>
                <td className="p-3"><StatusBadge status={row.status} /></td>
                <td className="p-3"><StatusBadge status={row.compliance_status} /></td>
                <td className="p-3 text-slate-600">
                  {row.failure_date ? new Date(row.failure_date).toLocaleDateString() : "-"}
                </td>
                <td className="p-3 text-slate-600">
                  {row.remedial_deadline ? new Date(row.remedial_deadline).toLocaleDateString() : "-"}
                </td>
                <td className="p-3 text-center font-medium">
                  <span className={row.days_left <= 7 ? "text-red-600" : row.days_left <= 14 ? "text-amber-600" : "text-slate-700"}>
                    {row.days_left ?? "-"}
                  </span>
                </td>
                <td className="p-3"><StatusBadge status={row.deadline_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="operational-dashboard">
      {/* Header */}
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="h-7 w-7 text-teal-600" />
            Operations & Compliance
          </h1>
          <p className="text-slate-500 mt-1">
            Live control view for jobs, compliance, and failed PPM remedials
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab("jobs")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all ${
              activeTab === "jobs"
                ? "bg-teal-600 text-white shadow-md"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            }`}
            data-testid="tab-jobs"
          >
            <ClipboardList className="h-4 w-4" />
            Jobs Dashboard
            {jobs.total > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === "jobs" ? "bg-teal-700" : "bg-slate-100"
              }`}>
                {jobs.total}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab("compliance")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all ${
              activeTab === "compliance"
                ? "bg-teal-600 text-white shadow-md"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            }`}
            data-testid="tab-compliance"
          >
            <Shield className="h-4 w-4" />
            Compliance Dashboard
            {compliance.data.length > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === "compliance" ? "bg-teal-700" : "bg-slate-100"
              }`}>
                {compliance.data.length}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab("cases")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all ${
              activeTab === "cases"
                ? "bg-teal-600 text-white shadow-md"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            }`}
            data-testid="tab-cases"
          >
            <AlertTriangle className="h-4 w-4" />
            Compliance Cases
            {cases.data.length > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === "cases" ? "bg-teal-700" : "bg-slate-100"
              }`}>
                {cases.data.length}
              </span>
            )}
          </button>
          
          {/* Quick Links */}
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => navigate('/operations/communication')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 text-sm font-medium transition-all"
              data-testid="link-communication"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Comms Metrics
            </button>
            <button
              onClick={() => navigate('/operations/routes')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm font-medium transition-all"
              data-testid="link-routes"
            >
              <Calendar className="h-4 w-4" />
              Routes
            </button>
            <button
              onClick={() => navigate('/operations/attendance')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 text-sm font-medium transition-all"
              data-testid="link-attendance"
            >
              <Building className="h-4 w-4" />
              Attendance
            </button>
            <button
              onClick={() => navigate('/operations/automation')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 text-sm font-medium transition-all"
              data-testid="link-automation"
            >
              <RefreshCw className="h-4 w-4" />
              Automation
            </button>
            <button
              onClick={() => navigate('/analytics')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm font-medium transition-all"
              data-testid="link-analytics"
            >
              <TrendingUp className="h-4 w-4" />
              Analytics
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6 shadow-sm">
          {activeTab === "jobs" && renderJobs()}
          {activeTab === "compliance" && renderComplianceDashboard()}
          {activeTab === "cases" && renderComplianceCases()}
        </div>
      </div>

      {/* Asset Jobs Dialog */}
      <Dialog open={!!selectedAsset} onOpenChange={() => setSelectedAsset(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-[#1F7A78]" />
              {selectedAsset?.asset_name || "Asset"} - Open Jobs
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {loadingAssetJobs ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-[#1F7A78] mr-2" />
                <span className="text-slate-600">Loading jobs...</span>
              </div>
            ) : assetJobs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <ClipboardList className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No open jobs found for this asset</p>
              </div>
            ) : (
              <div className="space-y-3">
                {assetJobs.map((job, idx) => (
                  <div 
                    key={job.id || job.internal_id || idx}
                    onClick={() => {
                      setSelectedAsset(null);
                      navigate(`/fm/jobs/${job.id || job.internal_id}`);
                    }}
                    className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-[#1F7A78] hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-mono text-sm text-[#1F7A78] font-semibold">
                          {job.job_code || job.job_id || "No Job Code"}
                        </div>
                        <div className="text-slate-700 font-medium mt-1">
                          {job.job_type || job.category || "General"}
                        </div>
                        <div className="text-sm text-slate-500 mt-1">
                          {job.site || job.park || "-"} · {job.unit || "-"}
                        </div>
                        {job.description && (
                          <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                            {job.description}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge className={
                          job.status === 'Scheduled' ? 'bg-blue-100 text-blue-700' :
                          job.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          job.status?.includes('Quote') ? 'bg-yellow-100 text-yellow-700' :
                          job.status === 'Assigned' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {job.status || "Unknown"}
                        </Badge>
                        {job.contractor_name && (
                          <div className="text-xs text-slate-500 mt-2">
                            {job.contractor_name}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                      <div className="text-xs text-slate-500">
                        {job.due_date ? `Due: ${new Date(job.due_date).toLocaleDateString()}` : "No due date"}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-[#1F7A78] font-medium">
                        View Details <ExternalLink className="h-3 w-3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
